import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Batch, DocumentItem } from '../../core/services/batch';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Button } from '../../shared/components/button/button';
import { IngestProgressModal } from '../../shared/components/ingest-progress-modal/ingest-progress-modal';
import { BatchCompletedModal } from '../../shared/components/batch-completed-modal/batch-completed-modal';
import { DeleteBatchModal } from '../../shared/components/delete-batch-modal/delete-batch-modal';
import { IngestionRealtime } from '../../core/services/ingestion-realtime';
import {
  batchStatusLabel,
  batchStatusClass,
  documentStatusLabel,
  isDocumentCompleted,
  isProcessing as checkIsProcessing,
  isDraft as checkIsDraft,
  isPreparing as checkIsPreparing,
  idsEqual
} from '../../shared/constants/batch-status';
import { formatDocumentErrorMessage } from '../../shared/constants/document-error-message';
import { BrandIcon } from '../../shared/components/brand-icon/brand-icon';
import { connectorPresentation } from '../brain/document-sources';

function isFailedStatus(status: string): boolean {
  return status === 'Failed';
}

@Component({
  selector: 'app-batch-detail',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, ToastComponent, Button, IngestProgressModal, BatchCompletedModal, DeleteBatchModal, BrandIcon],
  templateUrl: './batch-detail.html',
  styleUrl: './batch-detail.css'
})
export class BatchDetail implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private batchService = inject(Batch);
  private sanitizer = inject(DomSanitizer);
  private toast = inject(Toast);
  private realtime = inject(IngestionRealtime);
  private subscriptions = new Subscription();

  batchId = signal<string | null>(null);
  batch = signal<any | null>(null);
  documents = signal<DocumentItem[]>([]);
  isLoading = signal(true);
  now = signal(Date.now());
  private clock?: ReturnType<typeof setInterval>;
  expandedTimings = signal<Set<string>>(new Set());
  isLoadingPage = signal(false);

  // The backend clamps pageSize to a max of 200 (BatchesController.GetBatch), so a batch
  // with more documents than that genuinely can't come back in one response — page through
  // it instead of hardcoding a single large request.
  readonly docPageSize = 200;
  documentPage = signal(1);
  isStartingIngestion = signal(false);
  isDeleting = signal(false);

  showIngestingModal = signal(false);
  showCompletedModal = signal(false);
  showDeleteModal = signal(false);

  docPendingDelete = signal<DocumentItem | null>(null);
  deletingDocId = signal<string | null>(null);
  expandedErrorDocIds = signal<Set<string>>(new Set());

  preview = signal<DocumentPreview | null>(null);
  private previewRequest = 0;
  private previewUrl: string | null = null;

  ngOnInit() {
    this.clock = setInterval(() => this.now.set(Date.now()), 1000);
    this.batchId.set(this.route.snapshot.paramMap.get('id'));
    if (this.batchId()) {
      this.loadBatchDetail(this.batchId()!);
      this.startRealtime(this.batchId()!);
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.clock);
    const id = this.batchId();
    if (id) this.realtime.leaveBatch(id);
    this.subscriptions.unsubscribe();
    this.revokePreview();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.preview()) this.closePreview();
  }

  private loadBatchDetail(id: string, page: number = 1) {
    const isFirstLoad = page === 1 && !this.batch();
    isFirstLoad ? this.isLoading.set(true) : this.isLoadingPage.set(true);
    this.documentPage.set(page);

    this.batchService.getBatchById(id, page, this.docPageSize).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.isLoadingPage.set(false);
        this.batch.set(res.data.batch);
        this.documents.set(res.data.batch.documents || []);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.isLoadingPage.set(false);
        const message = err?.error?.message || 'Failed to load batch details.';
        this.toast.show(message, 'error');
      }
    });
  }

  get totalDocumentPages(): number {
    const b = this.batch();
    if (!b || !b.totalDocuments) return 1;
    return Math.max(1, Math.ceil(b.totalDocuments / this.docPageSize));
  }

  nextDocumentPage() {
    if (this.isLoadingPage() || this.documentPage() >= this.totalDocumentPages) return;
    this.loadBatchDetail(this.batchId()!, this.documentPage() + 1);
  }

  prevDocumentPage() {
    if (this.isLoadingPage() || this.documentPage() <= 1) return;
    this.loadBatchDetail(this.batchId()!, this.documentPage() - 1);
  }

  statusClass(status: string): string {
    return batchStatusClass(status);
  }

  isProcessing(status: string): boolean {
    return checkIsProcessing(status);
  }

  isPreparing(status: string): boolean {
    return checkIsPreparing(status);
  }

  isDraft(status: string): boolean {
    return checkIsDraft(status);
  }

  isFailed(status: string): boolean {
    return isFailedStatus(status);
  }

  isCompleted(status: string): boolean {
    return isDocumentCompleted(status);
  }

  docStatusLabel(status: string): string {
    return documentStatusLabel(status);
  }

  canStartIngestion(status: string): boolean {
    return this.isDraft(status) || this.isFailed(status) || status === 'Completed';
  }

  isRestart(status: string): boolean {
    return this.isFailed(status) || status === 'Completed';
  }

  canDelete(status: string): boolean {
    return !this.isProcessing(status) && !this.isPreparing(status) && !this.isDeleting();
  }

  deleteTooltip(status: string): string {
    if (this.isProcessing(status)) {
      return 'You can delete after the ingestion completes.';
    }
    if (this.isPreparing(status)) {
      return 'You can delete after preparation finishes.';
    }
    if (this.isDraft(status)) {
      return 'Delete this draft batch and its uploaded documents.';
    }
    return 'Delete this batch, its documents, and any indexed chunks.';
  }

  onStartIngestion() {
    const b = this.batch();
    if (!b || this.isStartingIngestion() || !this.canStartIngestion(b.status)) return;

    const isRestart = this.isRestart(b.status);
    this.isStartingIngestion.set(true);
    this.batchService.startIngestion(b.id).subscribe({
      next: (res) => {
        this.isStartingIngestion.set(false);
        this.toast.show(isRestart ? 'Batch ingestion restarted successfully.' : 'Batch ingestion started successfully.', 'success');
        const statusLabel = batchStatusLabel(res.data.status);
        this.batch.set({ ...b, status: statusLabel, processedDocuments: 0, failedDocuments: 0, percentComplete: 0 });
        this.documents.update(docs => docs.map(d => ({ ...d, status: 'Processing' })));
        this.showIngestingModal.set(true);
        void this.realtime.joinBatch(b.id);
      },
      error: (err) => {
        this.isStartingIngestion.set(false);
        const message = err?.error?.message || 'Failed to start ingestion. Please try again.';
        this.toast.show(message, 'error');
      }
    });
  }

  ingestPercent = computed(() => {
    const b = this.batch();
    if (!b) return 0;
    if (b.totalDocuments > 0) {
      return Math.min(100, Math.round(((b.processedDocuments + b.failedDocuments) / b.totalDocuments) * 100));
    }
    return Math.round(b.percentComplete ?? 0);
  });

  closeIngestingModal() {
    this.showIngestingModal.set(false);
  }

  closeCompletedModal() {
    this.showCompletedModal.set(false);
  }

  onViewAllBatches() {
    this.showCompletedModal.set(false);
    this.router.navigate(['/manage-batches']);
  }

  onDelete() {
    const b = this.batch();
    if (!b || !this.canDelete(b.status)) return;
    this.showDeleteModal.set(true);
  }

  closeDeleteModal() {
    if (this.isDeleting()) return;
    this.showDeleteModal.set(false);
  }

  confirmDelete() {
    const b = this.batch();
    // Use status guards directly — canDelete also checks isDeleting, which would
    // block if we ever set the flag before this guard.
    if (!b || this.isDeleting() || this.isProcessing(b.status) || this.isPreparing(b.status)) return;

    this.isDeleting.set(true);
    this.batchService.deleteBatch(b.id).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.showDeleteModal.set(false);
        this.toast.show('Batch deleted successfully.', 'success');
        this.router.navigate(['/manage-batches']);
      },
      error: (err) => {
        this.isDeleting.set(false);
        this.showDeleteModal.set(false);
        const message = err?.error?.message || 'Failed to delete batch. Please try again.';
        this.toast.show(message, 'error');
      }
    });
  }

  goBack() {
    this.router.navigate(['/manage-batches']);
  }

  canDeleteDocument(doc: DocumentItem): boolean {
    const b = this.batch();
    if (!b) return false;
    if (this.isProcessing(b.status) || this.isPreparing(b.status)) return false;
    return doc.status !== 'Processing' && this.deletingDocId() !== doc.id;
  }

  onDeleteDocument(doc: DocumentItem) {
    if (!this.canDeleteDocument(doc)) return;
    this.docPendingDelete.set(doc);
  }

  closeDeleteDocumentModal() {
    if (this.deletingDocId()) return;
    this.docPendingDelete.set(null);
  }

  confirmDeleteDocument() {
    const doc = this.docPendingDelete();
    const b = this.batch();
    if (!doc || !b || this.deletingDocId()) return;
    if (this.isProcessing(b.status) || this.isPreparing(b.status)) return;
    if (doc.status === 'Processing') return;

    this.deletingDocId.set(doc.id);
    this.batchService.deleteDocument(b.id, doc.id).subscribe({
      next: (res) => {
        this.deletingDocId.set(null);
        this.docPendingDelete.set(null);

        if (res?.data?.batchDeleted) {
          this.toast.show('Document deleted. This was the last document, so the batch was removed too.', 'success');
          this.router.navigate(['/manage-batches']);
          return;
        }

        this.documents.update(docs => docs.filter(d => d.id !== doc.id));

        const processedDelta = doc.status === 'Passed' ? 1 : 0;
        const failedDelta = doc.status === 'Failed' ? 1 : 0;
        this.batch.set({
          ...b,
          totalDocuments: Math.max(0, b.totalDocuments - 1),
          processedDocuments: Math.max(0, b.processedDocuments - processedDelta),
          failedDocuments: Math.max(0, b.failedDocuments - failedDelta)
        });

        this.toast.show('Document deleted successfully.', 'success');
      },
      error: (err) => {
        this.deletingDocId.set(null);
        this.docPendingDelete.set(null);
        const message = err?.error?.message || 'Failed to delete document. Please try again.';
        this.toast.show(message, 'error');
      }
    });
  }

  getExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  }

  isEmailItem(doc: DocumentItem): boolean {
    return doc.fileExtension === 'EMAIL';
  }

  getConnectorLabel(doc: DocumentItem): string {
    return this.connectorView(doc).label;
  }

  connectorIcon(doc: DocumentItem): string {
    return this.connectorView(doc).icon;
  }

  private connectorView(doc: DocumentItem): { label: string; icon: string } {
    const batch = this.batch();
    if (this.isEmailItem(doc) || batch?.sourceType === 'Mailbox') {
      return connectorPresentation('Mailbox');
    }
    const provider = batch?.sourceProvider
      || (batch?.sourceType === 'Upload' ? 'Upload' : null)
      || (doc.connector?.toLowerCase().includes('github') ? 'Github' : null)
      || batch?.sourceType;
    return connectorPresentation(provider);
  }

  getDocIconClass(fileName: string): string {
    const ext = this.getExtension(fileName);
    if (ext === 'DOCX' || ext === 'DOC') return 'doc-word';
    if (ext === 'XLSX' || ext === 'XLS' || ext === 'CSV') return 'doc-excel';
    if (ext === 'PPTX' || ext === 'PPT') return 'doc-ppt';
    return 'doc-generic';
  }

  // Until ingestion is kicked off, every document in a Draft batch is shown as Draft
  // regardless of whatever raw per-document status the backend has stored.
  docDisplayStatus(doc: DocumentItem): string {
    const b = this.batch();
    if (b && this.isDraft(b.status)) return 'Draft';
    return this.docStatusLabel(doc.status);
  }

  docStatusBadgeClass(doc: DocumentItem): string {
    const b = this.batch();
    if (b && this.isDraft(b.status)) return this.statusClass('Draft');
    return this.statusClass(doc.status);
  }

  docErrorMessage(doc: DocumentItem): string {
    return formatDocumentErrorMessage(doc.errorMessage);
  }

  hasDocError(doc: DocumentItem): boolean {
    return doc.status === 'Failed' && !!doc.errorMessage?.trim();
  }

  isErrorExpanded(docId: string): boolean {
    return this.expandedErrorDocIds().has(docId.toLowerCase());
  }

  toggleDocError(docId: string, event: Event): void {
    event.stopPropagation();
    const key = docId.toLowerCase();
    this.expandedErrorDocIds.update(set => {
      const next = new Set(set);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  private startRealtime(batchId: string): void {
    void this.realtime.joinBatch(batchId);
    this.registerRealtimeHandlers();
  }

  private registerRealtimeHandlers(): void {
    this.subscriptions.add(
      this.realtime.documentStatusChanged$.subscribe(payload => {
        if (!payload.documentId || !idsEqual(payload.batchId, this.batchId())) return;
        this.documents.update(docs => docs.map(d => idsEqual(d.id, payload.documentId)
          ? { ...d, status: payload.status ?? d.status, errorMessage: payload.errorMessage ?? d.errorMessage }
          : d
        ));
      })
    );

    this.subscriptions.add(
      this.realtime.batchProgress$.subscribe(payload => {
        const b = this.batch();
        if (!b || !idsEqual(payload.batchId, this.batchId())) return;
        this.batch.set({
          ...b,
          totalDocuments: payload.total ?? b.totalDocuments,
          processedDocuments: payload.processed ?? b.processedDocuments,
          failedDocuments: payload.failed ?? b.failedDocuments,
          percentComplete: payload.percentComplete ?? b.percentComplete
        });
      })
    );

    this.subscriptions.add(
      this.realtime.batchStarted$.subscribe(payload => {
        const b = this.batch();
        if (!b || !idsEqual(payload.batchId, this.batchId())) return;
        this.batch.set({ ...b, status: batchStatusLabel(payload.status), startedAt: new Date().toISOString(), completedAt: null });
      })
    );

    this.subscriptions.add(
      this.realtime.batchCompleted$.subscribe(payload => {
        const b = this.batch();
        if (!b || !idsEqual(payload.batchId, this.batchId())) return;

        const statusLabel = batchStatusLabel(payload.status);
        this.batch.set({
          ...b,
          status: statusLabel,
          completedAt: payload.completedAt ?? b.completedAt,
          percentComplete: statusLabel === 'Completed' || statusLabel === 'Failed' ? 100 : b.percentComplete
        });

        this.loadBatchDetail(this.batchId()!, this.documentPage());

        if (this.showIngestingModal()) {
          this.showIngestingModal.set(false);
          if (statusLabel === 'Completed') {
            this.showCompletedModal.set(true);
          }
        }
      })
    );
  }

  private static readonly STAGE_LABELS: Record<string, string> = {
    T1: 'Scrape',
    T2: 'OCR',
    T3: 'Chunk & embed',
    T4: 'Enrichment'
  };

  formatDuration(ms: number | null): string {
    if (ms === null || ms < 0) return '-';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${s}s`;
  }

  batchElapsed(b: any): string {
    if (!b?.startedAt) return '-';
    const start = Date.parse(b.startedAt);
    const end = b.completedAt ? Date.parse(b.completedAt) : this.now();
    return this.formatDuration(end - start);
  }

  versionLabel(doc: DocumentItem): string | null {
    return ({ new: 'New', update: 'Update', ignore: 'Ignore' } as Record<string, string>)[doc.versionAction ?? ''] ?? null;
  }

  /** e.g. "5 statements: 1 new (20%) · 2 duplicate (40%) · 2 updated (40%)" -- only the non-zero parts.
   *  A statement is a sentence of a paragraph, or a table row / list item. */
  versionSummary(doc: DocumentItem): string {
    if (!doc.versionStatsJson) return '';
    try {
      const s = JSON.parse(doc.versionStatsJson) as Record<string, number>;
      const parts = [['new', 'new'], ['duplicate', 'duplicate'], ['updated', 'updated']]
        .filter(([key]) => s[key] > 0)
        .map(([key, label]) => `${s[key]} ${label} (${s[key + '_pct']}%)`);
      if (s['lines_skipped_short'] > 0) parts.push(`${s['lines_skipped_short']} too short to compare`);
      if (s['removed'] > 0) parts.push(`${s['removed']} removed from the older version`);
      const total = s['lines_compared'] ?? 0;
      return `${total} ${total === 1 ? 'statement' : 'statements'}: ${parts.join(' · ')}`;
    } catch {
      return '';
    }
  }

  private parseStages(doc: DocumentItem): { stage: string; label: string; start: number; end: number }[] {
    if (!doc.stageTimingsJson) return [];
    try {
      const raw = JSON.parse(doc.stageTimingsJson) as Record<string, { start?: string; end?: string }>;
      return Object.entries(raw)
        .filter(([, t]) => t?.start && t?.end)
        .map(([stage, t]) => ({ stage, label: BatchDetail.STAGE_LABELS[stage] ?? stage, start: Date.parse(t.start!), end: Date.parse(t.end!) }))
        .sort((a, b) => a.start - b.start);
    } catch {
      return [];
    }
  }

  stageRows(doc: DocumentItem): { label: string; duration: string; wait: string }[] {
    const stages = this.parseStages(doc);
    return stages.map((s, i) => ({
      label: s.label,
      duration: this.formatDuration(s.end - s.start),
      wait: i === 0 ? '' : this.formatDuration(Math.max(0, s.start - stages[i - 1].end))
    }));
  }

  docElapsed(doc: DocumentItem): string {
    const stages = this.parseStages(doc);
    if (stages.length === 0) return '-';
    return this.formatDuration(Math.max(...stages.map(s => s.end)) - Math.min(...stages.map(s => s.start)));
  }

  hasTimings(doc: DocumentItem): boolean {
    return this.parseStages(doc).length > 0;
  }

  isTimingsExpanded(id: string): boolean {
    return this.expandedTimings().has(id);
  }

  toggleTimings(id: string): void {
    const next = new Set(this.expandedTimings());
    next.has(id) ? next.delete(id) : next.add(id);
    this.expandedTimings.set(next);
  }

  viewDocumentAccess(doc: any) {
    this.router.navigate(['/document-access', doc.id], {
      state: { docName: doc.fileName, batchId: this.batchId() }
    });
  }

  openDocument(doc: DocumentItem): void {
    const request = ++this.previewRequest;
    this.revokePreview();
    this.preview.set({
      fileName: doc.fileName,
      loading: true,
      kind: null,
      text: '',
      message: '',
      url: null,
      frameUrl: null
    });

    this.batchService.getDocumentContent(doc.id).subscribe({
      next: (res) => {
        if (request !== this.previewRequest) return;
        const data = res.data;
        if (data.kind === 'binary') {
          this.loadPreviewFile(doc, request, data.mediaType);
          return;
        }
        this.preview.set({
          fileName: data.fileName || doc.fileName,
          loading: false,
          kind: data.kind,
          text: data.text || '',
          message: data.message || '',
          url: null,
          frameUrl: null
        });
      },
      error: (err) => {
        if (request !== this.previewRequest) return;
        this.preview.set({
          fileName: doc.fileName,
          loading: false,
          kind: 'unavailable',
          text: '',
          message: err?.error?.message || 'Could not open this document.',
          url: null,
          frameUrl: null
        });
      }
    });
  }

  closePreview(): void {
    this.previewRequest++;
    this.revokePreview();
    this.preview.set(null);
  }

  private loadPreviewFile(doc: DocumentItem, request: number, mediaType: string | null): void {
    this.batchService.getDocumentFile(doc.id).subscribe({
      next: (blob) => {
        if (request !== this.previewRequest) return;
        const url = URL.createObjectURL(blob);
        this.previewUrl = url;
        const type = mediaType || blob.type || '';
        const image = type.startsWith('image/');
        this.preview.set({
          fileName: doc.fileName,
          loading: false,
          kind: image ? 'image' : 'pdf',
          text: '',
          message: '',
          url,
          frameUrl: image ? null : this.sanitizer.bypassSecurityTrustResourceUrl(url)
        });
      },
      error: () => {
        if (request !== this.previewRequest) return;
        this.preview.set({
          fileName: doc.fileName,
          loading: false,
          kind: 'unavailable',
          text: '',
          message: 'Could not open this document.',
          url: null,
          frameUrl: null
        });
      }
    });
  }

  private revokePreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }
}

interface DocumentPreview {
  fileName: string;
  loading: boolean;
  kind: 'text' | 'binary' | 'unavailable' | 'image' | 'pdf' | null;
  text: string;
  message: string;
  url: string | null;
  frameUrl: SafeResourceUrl | null;
}